import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AdminUsersIndex } from '@/types';

export function CreateAdminDialog() {
  const [open, setOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );

  const form = useForm({ email: '', fullName: '', role: 'guest' });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // `preserveState` keeps this dialog mounted across the redirect, which is what lets
    // the one-time password from the flash land in it (Inertia drops component state on
    // a successful POST otherwise).
    form.post('/admin/users', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: (page) =>
        setGeneratedPassword(
          (page.props as unknown as AdminUsersIndex).generatedPassword,
        ),
    });
  };

  const handleClose = () => {
    setOpen(false);
    setGeneratedPassword(null);
    reset();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Create Admin User
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Admin User</DialogTitle>
            <DialogDescription>
              Create a new admin user with a randomly generated password.
            </DialogDescription>
          </DialogHeader>

          {generatedPassword ? (
            <div className="space-y-4">
              <div className="rounded-md border border-green-600 bg-green-50 p-4">
                <h3 className="mb-2 font-semibold text-green-950 text-sm">
                  Admin User Created Successfully!
                </h3>
                <p className="mb-3 text-green-900 text-sm">
                  Please save this password securely. It will not be shown
                  again.
                </p>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 block font-medium text-green-950 text-xs">
                      Email:
                    </div>
                    <code className="block rounded border border-green-300 bg-white p-2 font-mono text-green-950 text-sm">
                      {data.email}
                    </code>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <div className="block font-medium text-green-950 text-xs">
                        Password:
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-green-700 text-xs hover:bg-green-100 hover:text-green-900"
                        onClick={() => {
                          navigator.clipboard.writeText(generatedPassword);
                        }}
                      >
                        Copy Password
                      </Button>
                    </div>
                    <code className="block rounded border border-green-300 bg-white p-2 font-mono text-green-950 text-sm">
                      {generatedPassword}
                    </code>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `Email: ${data.email}\nPassword: ${generatedPassword}`,
                    );
                  }}
                >
                  Copy All Credentials
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="create-admin-email">Email</Label>
                <Input
                  id="create-admin-email"
                  type="email"
                  placeholder="admin@example.com"
                  disabled={processing}
                  value={data.email}
                  onChange={(e) => setData('email', e.target.value)}
                />
                {errors.email && (
                  <p className="text-destructive text-sm">{errors.email}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="create-admin-full-name">Full Name</Label>
                <Input
                  id="create-admin-full-name"
                  placeholder="John Doe"
                  disabled={processing}
                  value={data.fullName}
                  onChange={(e) => setData('fullName', e.target.value)}
                />
                {errors.fullName && (
                  <p className="text-destructive text-sm">{errors.fullName}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="create-admin-role">Role</Label>
                <Select
                  value={data.role}
                  onValueChange={(value) => setData('role', value)}
                  disabled={processing}
                >
                  <SelectTrigger id="create-admin-role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Access</SelectItem>
                    <SelectItem value="guest">Guest</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-destructive text-sm">{errors.role}</p>
                )}
              </div>

              {errors.base && (
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="text-red-600 text-sm">{errors.base}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? 'Creating...' : 'Create Admin User'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
